import { useEffect, useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Can, PageHeader, Tabs, notify } from '@/kit/ui'
import { useCurrentUser } from '@/shell/useCurrentUser'
import { api, apiMode } from '@/lib/hubApi'
import { KeyManagement } from './KeyManagement'
import { UsageTab } from './UsageTab'
import { useSettingsStore } from '@/store/settingsStore'
import type { PortalSettings } from '@/types'

// Settings (S-1, owner 2026-10-18: "setting make proper tab view", "remove app mode"): one tab per subject, the tab in
// the address (/settings/runner), each tab saved on its own. Only settings that really change something are here
// (Cache time, Bundles kept / deleted and Alert emails did nothing and went; App mode went at the owner's word).
// S-2: Key management (consumers, scopes, limits, keys) and Usage (what each consumer called).

type TabId = 'general' | 'keys' | 'usage' | 'runner' | 'retention'
const TABS: { id: TabId; label: string }[] = [
  { id: 'general', label: 'General' },
  { id: 'keys', label: 'Key management' },
  { id: 'usage', label: 'Usage' },
  { id: 'runner', label: 'Runner' },
  { id: 'retention', label: 'Retention & alerts' },
]

const num = (v: string, min: number, max: number) => Math.max(min, Math.min(max, Math.round(Number(v) || min)))
const isUrl = (v: string) => /^https?:\/\/[^\s/]+/.test(v)

export function SettingsScreen() {
  const { tab } = useParams()
  const navigate = useNavigate()
  const active: TabId = TABS.some((t) => t.id === tab) ? (tab as TabId) : 'general'
  const [dirty, setDirty] = useState(false)
  const { can } = useCurrentUser()
  const editable = can('settings.manage')
  const go = (t: TabId) => {
    if (t === active) return
    if (dirty && !window.confirm('Leave this tab? Your changes here are not saved.')) return
    setDirty(false)
    navigate(`/settings/${t}`)
  }
  return (
    <>
      <PageHeader title="Settings" subtitle={editable ? 'Admins only. Each tab is saved on its own; every change writes History.' : 'Read-only for your role.'} />
      <Tabs tabs={TABS} value={active} onChange={go} ariaLabel="Settings sections" panelId="settings" />
      <div className="mt-4" id="settings-panel" role="tabpanel" data-testid={`settings-${active}`}>
        {active === 'general' && <GeneralTab onDirty={setDirty} />}
        {active === 'keys' && <KeysTab />}
        {active === 'usage' && <UsageTab />}
        {active === 'runner' && <RunnerTab onDirty={setDirty} />}
        {active === 'retention' && <RetentionTab onDirty={setDirty} />}
      </div>
    </>
  )
}

/** One tab's own copy of its settings, its Save, and whether it has changes. */
function useTabForm<K extends keyof PortalSettings>(keys: K[], onDirty: (d: boolean) => void, what: (s: Pick<PortalSettings, K>) => string) {
  const { user, can } = useCurrentUser()
  const saved = useSettingsStore((s) => s.settings)
  const update = useSettingsStore((s) => s.update)
  const pick = (s: PortalSettings) => Object.fromEntries(keys.map((k) => [k, s[k]])) as Pick<PortalSettings, K>
  const [form, setForm] = useState(() => pick(saved))
  useEffect(() => setForm(pick(saved)), [saved])
  const dirty = JSON.stringify(form) !== JSON.stringify(pick(saved))
  useEffect(() => onDirty(dirty), [dirty, onDirty])
  const save = () => { update(form, user.email); notify.success('Settings saved', what(form)) }
  return { form, setForm, dirty, save, editable: can('settings.manage'), saved }
}

function Card({ title, children, hint }: { title: string; children: ReactNode; hint?: ReactNode }) {
  return (
    <section className="card max-w-3xl space-y-3">
      <h2 className="section-title !mb-0">{title}</h2>
      {hint && <p className="text-2xs text-muted">{hint}</p>}
      {children}
    </section>
  )
}

function SaveBar({ dirty, disabled, onSave }: { dirty: boolean; disabled?: boolean; onSave: () => void }) {
  return (
    <Can permission="settings.manage">
      <div className="flex items-center gap-2">
        <button type="button" className="btn btn-primary" disabled={!dirty || disabled} onClick={onSave} data-testid="settings-save">Save</button>
        {dirty && <span className="text-2xs font-semibold text-amber-600 dark:text-amber-300">● not saved</span>}
      </div>
    </Can>
  )
}

function NumberField({ label, value, onChange, id, suffix, disabled }: { label: string; value: number; onChange: (v: string) => void; id: string; suffix?: string; disabled: boolean }) {
  return (
    <div>
      <label className="label" htmlFor={id}>{label}</label>
      <div className="flex items-center gap-1.5">
        <input id={id} className="input w-28" inputMode="numeric" disabled={disabled} value={value} onChange={(e) => onChange(e.target.value)} />
        {suffix && <span className="text-2xs text-muted">{suffix}</span>}
      </div>
    </div>
  )
}

function GeneralTab({ onDirty }: { onDirty: (d: boolean) => void }) {
  const { form, setForm, dirty, save, editable } = useTabForm(['hubUrls'], onDirty, (s) => `UAT ${s.hubUrls.uat} · production ${s.hubUrls.production}`)
  const field = (label: string, key: 'uat' | 'production', id: string) => (
    <div>
      <label className="label" htmlFor={id}>{label}</label>
      <input id={id} className="input mono" disabled={!editable} value={form.hubUrls[key]} onChange={(e) => setForm({ hubUrls: { ...form.hubUrls, [key]: e.target.value.trim() } })} />
      {!isUrl(form.hubUrls[key]) && <p className="field-error">A full address starting with https://</p>}
    </div>
  )
  return (
    <Card title="Hub addresses" hint={<>Where the lenders' endpoints and the service API are called: <code>{'{address}'}/lenders/{'{lender}'}/v2/dedupe</code> and <code>{'{address}'}/v1/applications</code>. The API catalogue, its copy buttons, the Postman / OpenAPI files and the playground's "Built container" calls use them.</>}>
      <div className="grid grid-cols-1 gap-3">
        {field('UAT address', 'uat', 'set-hub-uat')}
        {field('Production address', 'production', 'set-hub-prod')}
      </div>
      <SaveBar dirty={dirty} disabled={!isUrl(form.hubUrls.uat) || !isUrl(form.hubUrls.production)} onSave={save} />
    </Card>
  )
}

/** The consumers with their scopes, limits and keys (S-2), and which key each Hub API takes. */
function KeysTab() {
  return (
    <div className="space-y-3">
      <KeyManagement />
      <Card title="Which key each Hub API takes" hint="Public: only /health. Every other API needs a key of a consumer with its scope; a key outside its scopes gets 403, over its limit 429.">
        <div className="overflow-x-auto">
          <table className="table">
            <thead><tr><th>API</th><th>Scope</th><th>Key goes in</th></tr></thead>
            <tbody>
              <tr><td className="mono">GET /health</td><td>public</td><td>no key</td></tr>
              <tr><td className="mono">GET /service/v1/app/catalog · KFS · logo</td><td>Catalog</td><td className="mono">Authorization: Bearer &lt;key&gt;</td></tr>
              <tr><td className="mono">POST /v1/applications</td><td>Applications</td><td className="mono">x-app-key</td></tr>
              <tr><td className="mono">POST /lenders/{'{slug}'}/{'{route}'} on the UAT address</td><td>Lenders · UAT</td><td className="mono">x-hub-key</td></tr>
              <tr><td className="mono">POST /lenders/{'{slug}'}/{'{route}'} on the production address</td><td>Lenders · production</td><td className="mono">x-hub-key</td></tr>
              <tr><td className="mono">/admin/v1/*</td><td>this portal</td><td>your sign-in</td></tr>
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}

function RunnerTab({ onDirty }: { onDirty: (d: boolean) => void }) {
  const { form, setForm, dirty, save, editable, saved } = useTabForm(['runner'], onDirty, (s) => `${s.runner.memoryMb} MB per container, ${s.runner.deadlineMs} ms per lender call`)
  const [parts, setParts] = useState<{ gitUrl: string; storageUrl: string; registry: string | null } | null>(null)
  const { can } = useCurrentUser()
  const seesStorage = can('gateway.view')
  useEffect(() => { if (apiMode() && seesStorage) api<{ gitUrl: string; storageUrl: string; registry: string | null }>('GET', '/storage').then(setParts, () => setParts(null)) }, [seesStorage])
  return (
    <div className="space-y-3">
      <Card title="Limits of every lender container" hint="Each batch runs in its own Node container (the playground's sandbox and the live containers alike). A call that goes over these ends as an error.">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <NumberField label="Memory per container" value={form.runner.memoryMb} onChange={(v) => setForm({ runner: { ...form.runner, memoryMb: num(v, 128, 2048) } })} id="set-mem" suffix="MB (128–2048)" disabled={!editable} />
          <NumberField label="Time per lender call" value={form.runner.deadlineMs} onChange={(v) => setForm({ runner: { ...form.runner, deadlineMs: num(v, 1000, 30000) } })} id="set-deadline" suffix="ms (1000–30000)" disabled={!editable} />
        </div>
        <p className="text-2xs text-muted">In force now: {saved.runner.memoryMb} MB, {saved.runner.deadlineMs} ms. Keys reach a container as variables from the vault, never on disk in git.</p>
        <SaveBar dirty={dirty} onSave={save} />
      </Card>
      <Card title="Where the code lives">
        <dl className="grid grid-cols-[150px_1fr] gap-x-3 gap-y-1.5 text-xs">
          <dt className="text-muted">Outgoing IP</dt><dd>the address lenders whitelist: the playground → API client → <b>What is our IP?</b> (on the server, the server's IP)</dd>
          <dt className="text-muted">Git (code)</dt><dd className="mono break-all">{apiMode() ? (parts?.gitUrl ?? '—') : 'http://git.internal:3000'}</dd>
          <dt className="text-muted">Bundles</dt><dd className="mono break-all">{apiMode() ? (parts?.storageUrl ?? '—') : 's3://hub-media (RustFS)'}</dd>
          <dt className="text-muted">Image registry</dt><dd className="mono break-all">{apiMode() ? (parts?.registry ?? '—') : 'localhost:5030'}</dd>
        </dl>
        <p className="text-2xs text-muted">Every built version is kept (bundle, image and git tag), so any version can be made live again.</p>
      </Card>
    </div>
  )
}

function RetentionTab({ onDirty }: { onDirty: (d: boolean) => void }) {
  const { form, setForm, dirty, save, editable } = useTabForm(['runLogDays', 'errorRule'], onDirty, (s) => `run log ${s.runLogDays} days; error rule ${s.errorRule.errors} in ${s.errorRule.minutes} min`)
  return (
    <div className="space-y-3">
      <Card title="Run log" hint="Every workflow run: steps, answer, reference ids, no personal data. Older runs are deleted (checked every 6 hours).">
        <NumberField label="Run log kept" value={form.runLogDays} onChange={(v) => setForm({ ...form, runLogDays: num(v, 1, 90) })} id="set-runlog" suffix="days (1–90)" disabled={!editable} />
      </Card>
      <Card title="Alert rule: endpoint errors" hint="An Alert is raised when one endpoint has this many errors within this time. The other two rules (a new lender code, a lender switched off) need no setting. Alerts show on the Alerts screen.">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <NumberField label="Error rule: errors" value={form.errorRule.errors} onChange={(v) => setForm({ ...form, errorRule: { ...form.errorRule, errors: num(v, 1, 1000) } })} id="set-err" suffix="per endpoint" disabled={!editable} />
          <NumberField label="… within" value={form.errorRule.minutes} onChange={(v) => setForm({ ...form, errorRule: { ...form.errorRule, minutes: num(v, 1, 1440) } })} id="set-min" suffix="minutes" disabled={!editable} />
        </div>
      </Card>
      <SaveBar dirty={dirty} onSave={save} />
    </div>
  )
}
